const CSRF_TOKEN_NAME = "csrf-token";

export class ApiError extends Error {
  constructor(message, status, payload = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
  }
}

function readCsrfToken() {
  const tokenMeta = document.querySelector(`meta[name="${CSRF_TOKEN_NAME}"]`);
  return tokenMeta?.getAttribute("content");
}

function stringifyForServer(payload) {
  return JSON.stringify(payload);
}

function flattenErrors(errors) {
  if (Array.isArray(errors)) {
    return errors.filter(Boolean).join(", ");
  }

  if (!errors || typeof errors !== "object") {
    return null;
  }

  return Object.entries(errors)
    .map(([key, value]) => {
      const message = Array.isArray(value) ? value.filter(Boolean).join(", ") : String(value);
      return `${key}: ${message}`;
    })
    .filter(Boolean)
    .join("; ");
}

function extractErrorMessage(status, payload, rawBody = null) {
  if (status === 401 || status === 403) {
    return "Your session is not authorized for this action. Please sign in again.";
  }

  if (status === 302) {
    return "Session required. Please sign in and try again.";
  }

  if (!payload) {
    return rawBody ? "Request failed." : "Request failed with no detail.";
  }

  if (typeof payload.error === "string") {
    return payload.error;
  }

  const flattened = flattenErrors(payload.errors);
  if (flattened) {
    return flattened;
  }

  if (typeof payload.message === "string") {
    return payload.message;
  }

  return "Request failed with no detail.";
}

export async function apiRequest(path, options = {}) {
  const { body: rawBody, headers: customHeaders, ...fetchOptions } = options;
  const method = fetchOptions.method || "GET";
  // FormData is passed through raw so the browser sets the multipart boundary.
  const isFormData = typeof FormData !== "undefined" && rawBody instanceof FormData;

  const headers = {
    Accept: "application/json",
    ...(method !== "GET" && rawBody && !isFormData ? { "Content-Type": "application/json" } : {}),
    ...(readCsrfToken() ? { "X-CSRF-Token": readCsrfToken() } : {}),
    ...(customHeaders || {})
  };

  const response = await fetch(path, {
    method,
    credentials: "same-origin",
    headers,
    ...(method.toUpperCase() !== "GET" && rawBody
      ? { body: isFormData ? rawBody : stringifyForServer(rawBody) }
      : {}),
    ...fetchOptions
  });

  if (response.status === 204) {
    return null;
  }

  const text = await response.text();
  let payload = null;

  if (text) {
    try {
      payload = JSON.parse(text);
    } catch (_error) {
      payload = null;
    }
  }

  if (!response.ok) {
    throw new ApiError(extractErrorMessage(response.status, payload, text), response.status, payload);
  }

  return payload;
}
